import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';
import { cn } from '../lib/classnames.js';

/**
 * Reveal — the app's single entrance gesture.
 *
 * Wraps a block in the `.reveal` rise animation (globals.css) and triggers it
 * when the block scrolls into view. Above-the-fold content fires on the first
 * frame, so the same component choreographs both page-open staggers and
 * scroll-reveals further down.
 *
 *   <Reveal delay={120}>…</Reveal>
 *
 * Notes:
 *   - Plays once. Re-entering the viewport does not replay it; the page should
 *     settle, not perform on every scroll.
 *   - If IntersectionObserver is unavailable, content shows immediately.
 *   - prefers-reduced-motion collapses the whole thing in CSS.
 */

interface RevealProps {
  children: ReactNode;
  /** Stagger offset in ms. Keep steps in the 60–120ms range. */
  delay?: number;
  /** Rendered element — use 'li', 'section', etc. to keep semantics intact. */
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
}

export function Reveal({ children, delay = 0, as: Tag = 'div', className, style }: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    let cleanup = (): void => {};
    const reveal = (): void => {
      setInView(true);
      cleanup();
    };

    // Direct geometry check — does NOT depend on the observer firing. Some
    // webviews (notably iOS Safari / in-app Chrome) deliver
    // IntersectionObserver callbacks unreliably, which would otherwise strand
    // a below-the-fold block at opacity:0 forever — visible as a slab of empty
    // space where its content should be. Re-checking on scroll/resize reveals
    // each block exactly when it enters view, independent of the observer.
    const check = (): void => {
      const r = node.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) reveal();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) reveal();
      },
      // Fire a touch before the block fully enters, so the rise reads as the
      // content meeting the reader rather than chasing them.
      { rootMargin: '0px 0px -6% 0px', threshold: 0.05 },
    );
    observer.observe(node);
    const onScroll = (): void => check();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    const raf = requestAnimationFrame(check);
    // Last-resort safety net: never leave content invisible past a beat, even
    // if both the observer and scroll events fail us.
    const fallback = window.setTimeout(() => setInView(true), 2500);

    cleanup = (): void => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(raf);
      window.clearTimeout(fallback);
    };
    return cleanup;
  }, []);

  return (
    <Tag
      // ElementType + ref covariance: every tag we pass renders an HTMLElement.
      ref={ref as never}
      className={cn('reveal', inView && 'is-in', className)}
      style={{ ...style, '--reveal-delay': `${delay}ms` } as CSSProperties}
    >
      {children}
    </Tag>
  );
}
